/**
 * AddToCollectionDialog.jsx — "Save to collection" for a RotoTube media item.
 *
 * Lists the team's collections (click to add the item) and offers an inline
 * "New collection" field. Works from both a card's kebab menu and the player,
 * so it's a dialog rather than an anchored menu. `descriptor` is the media to add.
 */

import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItemButton,
  ListItemText, TextField, Box, Typography, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import { Add as AddIcon, PlaylistAddCheck as AddedIcon } from '@mui/icons-material';
import { useCollections, useAddToCollection, useCreateCollection } from '../../hooks/useRotoTube';

export default function AddToCollectionDialog({ open, onClose, descriptor, onAdded }) {
  const { data: collections, isLoading } = useCollections();
  const addToCollection = useAddToCollection();
  const createCollection = useCreateCollection();
  const [newName, setNewName] = useState('');
  const [addedTo, setAddedTo] = useState(null);

  const add = async (collectionId, name) => {
    if (!descriptor) return;
    await addToCollection.mutateAsync({ collectionId, descriptor });
    setAddedTo(collectionId);
    onAdded?.(name);
    setTimeout(onClose, 550);
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    // Pending guard: Enter can fire repeatedly before the create resolves,
    // which would mint duplicate collections.
    if (!name || createCollection.isPending || addToCollection.isPending) return;
    const col = await createCollection.mutateAsync({ name });
    setNewName('');
    await add(col.id, name);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: '#212121', backgroundImage: 'none', color: '#f1f1f1' } }}>
      <DialogTitle>Save to collection</DialogTitle>
      <DialogContent sx={{ px: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {collections?.length ? (
              <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
                {collections.map((c) => (
                  <ListItemButton key={c.id} onClick={() => add(c.id, c.name)} disabled={addToCollection.isPending}>
                    <ListItemText primary={c.name} secondary={`${c.itemCount} ${c.itemCount === 1 ? 'item' : 'items'}`}
                      secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }} />
                    {addedTo === c.id && <AddedIcon sx={{ color: '#4ade80' }} />}
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', px: 1, py: 1 }}>No collections yet — create one below.</Typography>
            )}
            <TextField
              placeholder="New collection name" value={newName} size="small" fullWidth variant="filled"
              sx={{ mt: 1.5 }} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createAndAdd(); }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={createAndAdd} disabled={!newName.trim() || createCollection.isPending} sx={{ color: '#f1f1f1' }}>
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
